import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Mail, ArrowLeft, AlertCircle, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { logger } from '../../lib/logger';

export function ForgotPasswordForm() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err: any) {
      logger.error('[ForgotPasswordForm] Reset error:', err);
      setError(err.message || 'Error al enviar el correo de recuperación. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl text-center"
      >
        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 size={40} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-[#111827] mb-3">
          ¡Correo enviado!
        </h2>
        <p className="text-[#6B7280] mb-6">
          Hemos enviado un enlace de recuperación a <strong>{email}</strong>.
          Por favor revisa tu bandeja de entrada.
        </p>
        <Link
          to="/"
          className="inline-block px-6 py-3 bg-[#5D1828] text-white rounded-lg font-medium hover:bg-[#7A2235] transition-colors shadow-lg shadow-[#5D1828]/30"
        >
          Volver al inicio de sesión
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl"
    >
      <Link
        to="/"
        className="flex items-center gap-2 text-[#6B7280] hover:text-[#111827] mb-6 transition-colors"
      >
        <ArrowLeft size={20} />
        <span className="text-sm font-medium">Volver</span>
      </Link>

      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-[#5D1828] to-[#7A2235] rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Mail size={32} className="text-white" />
        </div>
        <h1 className="text-3xl font-bold text-[#111827] mb-2">
          Recuperar contraseña
        </h1>
        <p className="text-[#6B7280]">
          Te enviaremos un enlace para restablecer tu contraseña
        </p>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-700 text-sm"
        >
          <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-[#374151] mb-2">
            Correo electrónico
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={20} />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#5D1828] focus:border-transparent transition-all"
              placeholder="tu@email.com"
              required
              disabled={loading}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-[#5D1828] text-white rounded-lg font-medium hover:bg-[#7A2235] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-[#5D1828]/30 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Enviando...
            </>
          ) : (
            <>
              <Mail size={20} />
              Enviar enlace de recuperación
            </>
          )}
        </button>
      </form>
    </motion.div>
  );
}
